import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, BoxExtendedProps, Preloader } from '@common-fe/common-fe';
import styled from 'styled-components';

import { FileType } from '@/common/constants';
import { getBlob } from '@/modules/ReimburseMe/hooks/useFileUploadQuery';
import useConvertTiffToPng from '@/utils/hooks/useConvertTiffToPng';
import { getFileType } from '@/utils/modifiers/getFileType';

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: ${({ theme }) => theme.rounds.button2Round};
`;

const PDFEmbed = styled.embed`
  width: 100%;
  height: 100%;
  border-radius: ${({ theme }) => theme.rounds.button2Round};
  pointer-events: none;
`;

const IconPreview = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

interface Props extends BoxExtendedProps {
  fileUrl?: string;
  testId?: string;
}

const FilePreview: React.FC<Props> = ({ fileUrl, testId = 'file-preview', ...rest }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [blobUrl, setBlobUrl] = useState<string>('');

  const fileType = useMemo(() => {
    if (!fileUrl) return null;
    return getFileType(fileUrl);
  }, [fileUrl]);

  const isPDF = useMemo(() => fileType === FileType.PDF, [fileType]);
  const isTiff = useMemo(() => fileType === FileType.TIF || fileType === FileType.TIFF, [fileType]);
  const isImage = useMemo(
    () =>
      fileType === FileType.JPEG ||
      fileType === FileType.JPG ||
      fileType === FileType.PNG ||
      fileType === FileType.HEIC ||
      fileType === FileType.HEIF,
    [fileType]
  );

  // Convert TIFF to PNG (first page only)
  const { pngDocuments, isLoading: isTiffLoading } = useConvertTiffToPng({
    url: isTiff ? fileUrl : undefined,
  });

  // Fetch blob for PDF files
  const fetchBlob = useCallback(async () => {
    if (!fileUrl || !isPDF || blobUrl) return;

    setIsLoading(true);
    try {
      const blob = await getBlob(fileUrl);
      const url = URL.createObjectURL(blob);
      setBlobUrl(url);
      setHasError(false);
    } catch (error) {
      console.error('Failed to load PDF:', error);
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, [fileUrl, isPDF, blobUrl]);

  useEffect(() => {
    if (isPDF) {
      fetchBlob();
    }

    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileUrl, isPDF]);

  const handleImageLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleImageError = () => {
    console.error('Failed to load image:', fileUrl);
    setIsLoading(false);
    setHasError(true);
  };

  const renderContent = () => {
    if (isLoading || isTiffLoading) {
      return (
        <Box
          width="100%"
          height="100%"
          justify="center"
          align="center"
          style={{ position: 'absolute', top: 0, left: 0 }}
          data-testid={`${testId}_loading`}
        >
          <Preloader />
        </Box>
      );
    }

    if (!fileUrl) {
      return <IconPreview data-testid={`${testId}_fallback-icon`}></IconPreview>;
    }

    if (hasError) {
      return <IconPreview data-testid={`${testId}_error-icon`}></IconPreview>;
    }

    // Display TIFF as converted PNG (first page)
    if (isTiff && pngDocuments && pngDocuments.length > 0) {
      return (
        <PreviewImage
          data-testid={`${testId}_tiff-image`}
          src={pngDocuments[0].link}
          alt="File preview"
          onError={() => setHasError(true)}
        />
      );
    }

    // Display PDF using embed (shows first page)
    if (isPDF && blobUrl) {
      return <PDFEmbed data-testid={`${testId}_pdf-embed`} src={blobUrl} type="application/pdf" />;
    }

    // Display regular images
    if (isImage) {
      return (
        <PreviewImage
          data-testid={`${testId}_image`}
          src={fileUrl}
          alt="File preview"
          onLoad={handleImageLoad}
          onError={handleImageError}
          crossOrigin="anonymous"
        />
      );
    }

    return <IconPreview data-testid={`${testId}_unknown-icon`}></IconPreview>;
  };

  return (
    <Box
      data-testid={`${testId}_container`}
      height="102px"
      width="76px"
      border={{ color: 'border1', size: '1px' }}
      background="canvas"
      round="button2Round"
      overflow="hidden"
      style={{ position: 'relative' }}
      {...rest}
    >
      {renderContent()}
    </Box>
  );
};

export default FilePreview;
